// @ts-strict-ignore
import Layout from 'antd/lib/layout';
import Space from 'antd/lib/space';
import { dayjs } from '@/extendedDayjs';
import * as React from 'react';
import { Today } from '../../timeHelpers';
import {
    AutomationStatus,
    AutomationType,
    PullRequestStatus,
    ReleaseStepKickoff,
    ReleaseStepType,
    ReleaseType,
    AutomationRun,
} from '../../types';
import { AppComponentProps, ReleaseStepComponentProps } from '../../typescriptHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { borderedIconForAutomation } from './AutomationTab';
import Text from 'antd/lib/typography/Text';
import { EditOutlined, LinkOutlined, RetweetOutlined, ThunderboltFilled } from '@ant-design/icons';
import {
    colorForAutomationStatus,
    displayTextForAutomationStatus,
    imperativeDescriptionTextForAutomationType,
} from '../../helpers/displayHelpers';
import { renderTextWithMarkdownLinks } from '../../helpers/automationHelpers';
import Button from 'antd/lib/button';
import Paragraph from 'antd/lib/typography/Paragraph';
import Steps from 'antd/lib/steps';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import {
    selectAppSettings,
    selectAutomationById,
    selectAutomationsById,
    selectRelease,
    selectShouldDisableAutomation,
} from '@/selectors/selectors';
import { AutomationPlanExecutionTooltipIcon } from '@/components/planRestrictions/planRestrictionHelperComponents';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import PullRequestComponent from '@/components/kickoff/PullRequestComponent';
import BranchingDiagram from '@/components/kickoff/BranchingDiagram';
import { useReleaseStep } from '@/utils/hooks/helpers';

interface AutomationsListProps extends ReleaseStepComponentProps {
    onShowAutomationSettings: (automationId: string) => void;
    isReleaseStep: boolean;
    automationRuns: AutomationRun[];
}

const AutomationRunsList: React.FC<AutomationsListProps> = (props: AutomationsListProps) => {
    const automationRuns = props.automationRuns;

    const appUsesStaticReleaseBranch = useSelector(
        (state: RootState) => selectAppSettings(state, props.appId).usesStaticReleaseBranchForNonHotfixes,
    );
    const releaseIsHotfix = useSelector(
        (state: RootState) => selectRelease(state, props.releaseId).type === ReleaseType.Hotfix,
    );

    const automationsById = useSelector((state: RootState) => selectAutomationsById(state, props.appId));

    const releaseStep = useReleaseStep(props);

    let releaseBranchExists = false;
    let releaseBranchUrl = null;
    let hasPerformedAnyCodePromotion = false;
    let releaseBranch = null;
    let workingBranch = null;

    if (props.isReleaseStep && releaseStep && releaseStep.type === ReleaseStepType.Kickoff) {
        const kickoffStep = releaseStep as ReleaseStepKickoff;

        releaseBranchExists = kickoffStep.data.doesReleaseBranchExist;
        releaseBranch = kickoffStep.data.releaseBranch;
        releaseBranchUrl = kickoffStep.data.releaseBranchUrl;
        workingBranch = kickoffStep.data.workingBranch;

        hasPerformedAnyCodePromotion = kickoffStep.data.hasPerformedAnyCodePromotion;
    }

    const baseBranch = workingBranch;

    const sequencedAutomations = automationRuns
        .filter((automation) => automation.isPartOfSequence)
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber);

    const unsequencedAutomations = automationRuns
        .filter((automation) => !automation.isPartOfSequence)
        .sort((a, b) => a.name.localeCompare(b.name));

    const hasSequencedAutomations = sequencedAutomations.length > 0;
    return (
        <Layout style={{ paddingTop: 24, paddingBottom: 24 }}>
            {hasSequencedAutomations && (
                <Steps direction="vertical">
                    {sequencedAutomations.map((automation, index) => {
                        const isEnabled = automationsById[automation.id]?.enabled;
                        const icon = borderedIconForAutomation(automation.status, automation.isLifecycle, isEnabled);
                        let status = null;
                        let showIcon = false;

                        switch (automation.status) {
                            case AutomationStatus.Failed:
                                status = 'error';
                                break;
                            case AutomationStatus.Success:
                                status = 'finish';
                                break;
                            case AutomationStatus.Skipped:
                            case AutomationStatus.Inactive:
                                status = null;
                                showIcon = true;
                                break;
                            case AutomationStatus.Pending:
                                status = 'wait';
                                break;
                            case AutomationStatus.Retrying:
                            case AutomationStatus.Blocked:
                            case AutomationStatus.Incomplete:
                                status = 'process';
                                break;
                        }

                        return (
                            <Steps.Step
                                key={index}
                                status={status}
                                icon={showIcon ? icon : null}
                                title={
                                    <AutomationRunsListItem
                                        orgId={props.orgId}
                                        appId={props.appId}
                                        {...automation}
                                        hideIcon={true}
                                        onShowAutomationSettings={props.onShowAutomationSettings}
                                        hasPerformedAnyCodePromotion={hasPerformedAnyCodePromotion}
                                        usesStaticReleaseBranch={appUsesStaticReleaseBranch}
                                        releaseBranchExists={releaseBranchExists}
                                        baseBranch={baseBranch}
                                        releaseBranch={releaseBranch}
                                        releaseBranchUrl={releaseBranchUrl}
                                        isHotfix={releaseIsHotfix}
                                    />
                                }
                            ></Steps.Step>
                        );
                    })}
                </Steps>
            )}
            {unsequencedAutomations.length > 0 && (
                <Space direction="vertical">
                    {hasSequencedAutomations && <Divider />}
                    {unsequencedAutomations.map((automation) => {
                        return (
                            <AutomationRunsListItem
                                orgId={props.orgId}
                                appId={props.appId}
                                {...automation}
                                onShowAutomationSettings={props.onShowAutomationSettings}
                                hasPerformedAnyCodePromotion={hasPerformedAnyCodePromotion}
                                usesStaticReleaseBranch={appUsesStaticReleaseBranch}
                                releaseBranchExists={releaseBranchExists}
                                baseBranch={baseBranch}
                                releaseBranch={releaseBranch}
                                releaseBranchUrl={releaseBranchUrl}
                                isHotfix={releaseIsHotfix}
                            />
                        );
                    })}
                </Space>
            )}
        </Layout>
    );
};

export default AutomationRunsList;

interface AutomationRunsListProps extends AppComponentProps, AutomationRun {
    onShowAutomationSettings: (id: string) => void;
    // properties needed to render branching diagram
    usesStaticReleaseBranch: boolean;
    hasPerformedAnyCodePromotion: boolean;
    releaseBranchExists: boolean;
    baseBranch: string;
    releaseBranch?: string;
    releaseBranchUrl?: string;
    isHotfix: boolean;
    hideIcon?: boolean;
}

const AutomationRunsListItem: React.FC<AutomationRunsListProps> = (props: AutomationRunsListProps) => {
    const automationData = useSelector((state: RootState) => {
        const automationConfig = selectAutomationById(state, props.appId, props.id);

        if (!automationConfig) {
            return null;
        }

        return {
            automationName: automationConfig.name,
            isEnabled: automationConfig.enabled,
        };
    }, shallowEqual);

    const shouldDisableAutomationForPlan = useSelector((state: RootState) =>
        selectShouldDisableAutomation(state, props.orgId, props.type),
    );

    if (!automationData) {
        return null;
    }

    const { automationName, isEnabled } = automationData;

    const planBlocksExecution = shouldDisableAutomationForPlan && isEnabled;
    const statusHeadlineText =
        planBlocksExecution && props.status === AutomationStatus.Inactive ?
            'Disabled'
        :   displayTextForAutomationStatus(props.status, props.isRetrying);

    const showDateUntilExecution = props.scheduledAt && Today.isBefore(dayjs(props.scheduledAt), 'minute');

    let timeUntilExecution = null;

    if (showDateUntilExecution) {
        timeUntilExecution = dayjs(props.scheduledAt).fromNow();
    }

    const showPendingMessage = !(props.status === AutomationStatus.Pending && !!timeUntilExecution);

    const leftIcon = borderedIconForAutomation(props.status, props.isLifecycle, isEnabled);

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {!props.hideIcon && leftIcon}
            <div style={{ flexGrow: 1, display: 'flex' }}>
                <div
                    style={{
                        backgroundColor: antGrey.grey2,
                        borderColor: antGrey.grey4,
                        borderStyle: 'solid',
                        borderRadius: 12,
                        borderWidth: 1,
                        display: 'flex',
                        paddingLeft: 24,
                        paddingRight: 24,
                        paddingTop: 12,
                        paddingBottom: 12,
                        flexDirection: 'column',
                        marginLeft: 24,
                        minWidth: 700,
                        marginBottom: 24,
                        flexGrow: 1,
                    }}
                >
                    <div
                        style={{
                            borderBottomStyle: 'solid',
                            borderBottomColor: antGrey.grey4,
                            borderBottomWidth: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Space style={{ paddingTop: 12, paddingBottom: 12 }} align="center" size={10}>
                            <Space size={4} align="center">
                                <ThunderboltFilled style={{ fontSize: 16, color: runwayColors.info }} />
                                {props.autoScheduledFromCadence === true && (
                                    <Tooltip
                                        title={`The date for this automation ${
                                            props.scheduledAt ? 'was' : 'will be'
                                        } set by Runway according to your release schedule cadence settings.`}
                                    >
                                        <RetweetOutlined
                                            style={{
                                                fontSize: 20,
                                                color: runwayColors.info,
                                            }}
                                        />
                                    </Tooltip>
                                )}
                            </Space>
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    columnGap: 8,
                                }}
                            >
                                <Text
                                    style={{
                                        color: antGrey.grey8,
                                        fontSize: 16,
                                        fontWeight: 400,
                                        lineHeight: '24px',
                                        marginBottom: 0,
                                    }}
                                >
                                    {automationName}
                                </Text>
                                <AutomationPlanExecutionTooltipIcon visible={planBlocksExecution} />
                            </span>
                        </Space>
                        {props.hasEditableSettings && (
                            <Button
                                icon={<EditOutlined style={{ color: runwayColors.info }} />}
                                onClick={() => props.onShowAutomationSettings(props.id)}
                            >
                                Settings
                            </Button>
                        )}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            paddingTop: 24,
                            paddingBottom: 24,
                            justifyContent: 'space-between',
                            alignItems: 'stretch',
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: runwayColors.white,
                                borderRadius: 12,
                                paddingTop: 12,
                                paddingBottom: 12,
                                paddingLeft: 24,
                                paddingRight: 24,
                                marginRight: 12,
                                maxWidth: '50%',
                            }}
                        >
                            <Paragraph
                                type="secondary"
                                ellipsis={{ rows: 3 }}
                                style={{ marginBottom: 0, lineHeight: 1.5, fontSize: 14 }}
                            >
                                {props.description}
                            </Paragraph>
                        </div>
                        {props.type === AutomationType.AutoKickoffRelease && (
                            <BranchingDiagram
                                usesStaticReleaseBranch={props.usesStaticReleaseBranch}
                                hasPerformedAnyCodePromotion={props.hasPerformedAnyCodePromotion}
                                releaseBranchExists={props.releaseBranchExists}
                                base={props.baseBranch}
                                releaseBranch={props.releaseBranch}
                                releaseBranchUrl={props.releaseBranchUrl}
                                isHotfix={props.isHotfix}
                            />
                        )}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                justifyContent: 'flex-end',
                                marginLeft: 12,
                                flexGrow: 1,
                                minWidth: 200,
                            }}
                        >
                            {isEnabled && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-end',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    {showPendingMessage && (
                                        <Text
                                            style={{
                                                color: colorForAutomationStatus(props.status, props.isLifecycle),
                                                fontWeight: 600,
                                                fontSize: 18,
                                                textAlign: 'right',
                                            }}
                                        >
                                            {statusHeadlineText}
                                        </Text>
                                    )}
                                    {props.statusMessage && (
                                        <Paragraph
                                            ellipsis={{ rows: 3 }}
                                            style={{
                                                textAlign: 'right',
                                                marginBottom: 0,
                                                lineHeight: 1.5,
                                                fontSize: 14,
                                            }}
                                            type="secondary"
                                        >
                                            {renderTextWithMarkdownLinks(props.statusMessage)}
                                            {props.statusMessageLinkUrl && (
                                                <Button
                                                    type="link"
                                                    icon={<LinkOutlined />}
                                                    href={props.statusMessageLinkUrl}
                                                    target="_blank"
                                                />
                                            )}
                                        </Paragraph>
                                    )}
                                    {props.status === AutomationStatus.Pending && timeUntilExecution && (
                                        <Text
                                            style={{
                                                color:
                                                    props.isLifecycle ? runwayColors.statusYellow : runwayColors.info,
                                                fontWeight: 600,
                                                fontSize: 16,
                                                textAlign: 'right',
                                            }}
                                        >
                                            {imperativeDescriptionTextForAutomationType(props.type)}{' '}
                                            {timeUntilExecution}
                                        </Text>
                                    )}
                                    {props.relevantPRs.length > 0 && (
                                        <Space>
                                            {props.relevantPRs.map((pr) => {
                                                if (
                                                    props.type === AutomationType.AutoCherryPickFromWorkingBranch ||
                                                    (props.type === AutomationType.AutoBackmergeReleaseBranches &&
                                                        pr.status !== PullRequestStatus.Open)
                                                ) {
                                                    return null;
                                                }

                                                return <PullRequestComponent {...pr} />;
                                            })}
                                        </Space>
                                    )}
                                </div>
                            )}
                            {!isEnabled && (
                                <Text type="secondary" style={{ fontWeight: 600, fontSize: 16, textAlign: 'right' }}>
                                    Not enabled
                                </Text>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
